import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchInventoryComparisonData, InventoryComparisonResult } from '../../services/inventoryComparisonService';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

interface InventoryComparisonChartProps {
  location: string;
}

export const InventoryComparisonChart: React.FC<InventoryComparisonChartProps> = ({ location }) => {
  const [comparisonData, setComparisonData] = useState<InventoryComparisonResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<ChartJS<'line'>>(null);

  const loadData = useCallback(async () => {
    if (!location) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await fetchInventoryComparisonData(location);
      setComparisonData(result);
    } catch (err) {
      console.error('Error loading inventory comparison data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load inventory comparison data');
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    loadData();
  }, [loadData]);


  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        Loading inventory comparison data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '16px',
        color: '#ef4444'
      }}>
        Error: {error}
      </div>
    );
  }

  if (!comparisonData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        No inventory comparison data available for {location}
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      height: '100%',
      minHeight: '400px',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Line
        ref={chartRef}
        data={comparisonData.chartData}
        options={comparisonData.chartOptions}
      />
    </div>
  );
};
